import React, { useState, useRef } from 'react';
import { Container, CardContainer } from '../../../components/Container';
import { H1 } from '../../../components/Typography';
import { Button } from '../../../components/Button';
import api from '../../../lib/api';

function parseCsv(csvText) {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };
  const rawHeaders = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/^\s*["']|["']\s*$/g, ''));
  const headers = lines[0].split(',').map((h) => h.trim());
  const piIdx = rawHeaders.findIndex((h) => h === 'payment_intent_id' || h === 'payment intent id');
  const amountIdx = rawHeaders.findIndex((h) => h === 'amount_usd' || h === 'amount usd');
  if (piIdx === -1) return { headers: [], rows: [], error: 'CSV must have a column "payment_intent_id".' };

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map((v) => v.trim().replace(/^\s*["']|["']\s*$/g, ''));
    const payment_intent_id = values[piIdx] || '';
    const amount_usd = amountIdx >= 0 ? (values[amountIdx] ?? '') : '';
    if (payment_intent_id) rows.push({ payment_intent_id, amount_usd, _raw: values, _header: headers });
  }
  return { headers, rows, rawRows: lines.slice(1).map((line) => line.split(',').map((v) => v.trim())) };
}

function escapeCsvCell(value) {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const RESULT_COLUMNS = [
  'retry_status',
  'booking_id',
  'pi_status_before',
  'latest_charge_id',
  'comment',
  'retry_details',
];

function buildResultCsv(parseResult, results) {
  const { headers } = parseResult;
  const rows = parseResult.rows || [];
  const rowsToOutput = results.length > 0 && results.length < rows.length ? rows.slice(0, results.length) : rows;
  const outHeaders = [...headers, ...RESULT_COLUMNS];
  const headerLine = outHeaders.map(escapeCsvCell).join(',');
  const dataLines = rowsToOutput.map((row, i) => {
    const result = results[i];
    const values = row._raw ? [...row._raw] : [row.payment_intent_id, row.amount_usd];
    const retryDetailsJson =
      result?.retry_details != null
        ? JSON.stringify(result.retry_details)
        : '';
    const extra = result
      ? [
          result.status,
          result.booking_id ?? '',
          result.pi_status_before ?? '',
          result.latest_charge_id ?? '',
          result.comment,
          retryDetailsJson,
        ]
      : ['', '', '', '', '', ''];
    return [...values, ...extra].map(escapeCsvCell).join(',');
  });
  return [headerLine, ...dataLines].join('\n');
}

function downloadCsv(csvContent, filename = 'stripe-retry-result.csv') {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}


async function readStreamAsNDJSON(response, onLine) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        const data = JSON.parse(trimmed);
        onLine(data);
      } catch (_) {
        // skip malformed line
      }
    }
  }
  if (buffer.trim()) {
    try {
      onLine(JSON.parse(buffer.trim()));
    } catch (_) {}
  }
}

export default function RetryStripePayments() {
  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [log, setLog] = useState([]);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const fileInputRef = useRef(null);
  const listEndRef = useRef(null);

  React.useEffect(() => {
    if (results.length > 0 && listEndRef.current) listEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [results.length]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f);
    setParseResult(null);
    setResults([]);
    setLog([]);
    setError(null);
    setDownloadError(null);
    setCopySuccess(false);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result || '';
      const parsed = parseCsv(text);
      if (parsed.error) {
        setError(parsed.error);
        setParseResult(null);
      } else {
        setError(null);
        setParseResult(parsed);
      }
    };
    reader.readAsText(f, 'UTF-8');
  };

  const handleSubmit = async () => {
    if (!parseResult?.rows?.length) {
      setError('Please upload a CSV with at least one row and a payment_intent_id column.');
      return;
    }
    setLoading(true);
    setLog([]);
    setResults([]);
    setError(null);
    setDownloadError(null);
    setCopySuccess(false);
    try {
      const response = await api.retryStripePayments({
        rows: parseResult.rows.map((r) => ({
          payment_intent_id: r.payment_intent_id,
          amount_usd: r.amount_usd,
        })),
        dry_run: dryRun,
      });

      await readStreamAsNDJSON(response, (data) => {
        if (data.type === 'log') setLog((prev) => [...prev, data.message]);
        if (data.type === 'result') setResults((prev) => [...prev, data.result]);
        if (data.type === 'error') setError(data.message || 'Stream error');
        if (data.type === 'done' || data.type === 'error') setLoading(false);
      });

      setLoading(false);
    } catch (err) {
      setError(err?.message || 'Request failed.');
      setLoading(false);
    }
  };

  const handleDownloadResult = () => {
    if (!parseResult?.rows?.length || !results?.length) return;
    setDownloadError(null);
    try {
      const csv = buildResultCsv(parseResult, results);
      const name = file?.name ? file.name.replace(/\.csv$/i, '') + '-result.csv' : 'stripe-retry-result.csv';
      downloadCsv(csv, name);
    } catch (err) {
      setDownloadError(err?.message || 'Download or CSV generation failed. Use "Copy CSV to clipboard" below.');
    }
  };

  const handleCopyToClipboard = async () => {
    if (!parseResult?.rows?.length || !results?.length) return;
    setDownloadError(null);
    setCopySuccess(false);
    try {
      const csv = buildResultCsv(parseResult, results);
      await navigator.clipboard.writeText(csv);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      setDownloadError(err?.message || 'Copy to clipboard failed.');
    }
  };

  const totalRows = parseResult?.rows?.length ?? 0;
  const transactionList = parseResult?.rows ?? [];
  // When we have fewer results than rows (e.g. dry run: first 3 only), show only processed rows to avoid a long list of "—"
  const displayList = results.length > 0 && results.length < transactionList.length
    ? transactionList.slice(0, results.length)
    : transactionList;

  // Summary: counts and totals by status
  const summary = React.useMemo(() => {
    const byStatus = { succeeded: 0, failed: 0, error: 0, skipped: 0, requires_action: 0, dry_run: 0 };
    const amountsByCurrency = {};
    results.forEach((r) => {
      const s = r.status;
      if (s in byStatus) byStatus[s]++;
      else byStatus.skipped++;
      if (s === 'succeeded' && r.retry_details?.amount != null) {
        const currency = (r.retry_details.currency || 'aud').toUpperCase();
        if (!amountsByCurrency[currency]) amountsByCurrency[currency] = { count: 0, totalCents: 0 };
        amountsByCurrency[currency].count += 1;
        amountsByCurrency[currency].totalCents += Number(r.retry_details.amount);
      }
    });
    const succeededTotalByCurrency = Object.entries(amountsByCurrency).map(([currency, { count, totalCents }]) => ({
      currency,
      count,
      total: (totalCents / 100).toFixed(2),
    }));
    return { byStatus, succeededTotalByCurrency };
  }, [results]);

  const failedCount = (summary.byStatus.error || 0) + (summary.byStatus.failed || 0);

  return (
    <CardContainer>
      <Container size="large" spaceBelow="medium">
        <H1>Retry Stripe Payments</H1>
        <p style={{ marginBottom: 16, color: '#555' }}>
          Upload a CSV with columns <strong>payment_intent_id</strong> and optionally <strong>amount_usd</strong> (for reference only).
          Retry confirms the <strong>existing</strong> Payment Intent: the amount and currency are those already on the PI (e.g. AUD), not from the CSV.
          Results appear in the list below as they complete.
          Download the CSV with added columns: <strong>retry_status</strong>, <strong>booking_id</strong>, <strong>pi_status_before</strong>, <strong>latest_charge_id</strong>, <strong>comment</strong>, and <strong>retry_details</strong> (JSON).
        </p>

        <div style={{ marginBottom: 16 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ marginRight: 8 }}
          />
          {parseResult?.rows?.length > 0 && (
            <span style={{ marginLeft: 8 }}>{parseResult.rows.length} row(s) parsed.</span>
          )}
        </div>

        {error && <div style={{ color: '#c00', marginBottom: 16 }}>{error}</div>}
        {downloadError && <div style={{ color: '#c00', marginBottom: 16 }}>{downloadError}</div>}

        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            <span>Dry run (retrieve PIs only, no charge)</span>
          </label>
          <Button onClick={handleSubmit} disabled={loading || !parseResult?.rows?.length}>
            {loading ? `Processing… ${results.length}/${totalRows}` : dryRun ? 'Run (dry run)' : 'Retry payments'}
          </Button>
        </div>
        {dryRun && (
          <div style={{ marginBottom: 16, padding: 10, background: '#f0f7ff', border: '1px solid #b3d7ff', borderRadius: 4, fontSize: 13 }}>
            Dry run is on: only the <strong>first 3</strong> rows are processed (to avoid spamming the Stripe API). Each is retrieved and validated, but <strong>no confirm/charge</strong> is made.
          </div>
        )}

        {/* Summary */}
        {results.length > 0 && (
          <div style={{ marginBottom: 16, padding: 16, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8 }}>
            <strong>Summary</strong>
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <div>
                <span style={{ color: '#0d8050', fontWeight: 600 }}>Succeeded: {summary.byStatus.succeeded} payments</span>
                {summary.succeededTotalByCurrency.length > 0 && (
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    {summary.succeededTotalByCurrency.map(({ currency, total }) => (
                      <span key={currency} style={{ marginRight: 12 }}>{currency} {total}</span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <span style={{ color: '#c00', fontWeight: 600 }}>Failed (error): {failedCount} payments</span>
              </div>
              {((summary.byStatus.skipped || 0) + (summary.byStatus.requires_action || 0)) > 0 && (
                <div style={{ fontSize: 13, color: '#666' }}>
                  Skipped: {summary.byStatus.skipped || 0} · Requires action: {summary.byStatus.requires_action || 0}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Live transaction list */}
        {(loading || results.length > 0) && totalRows > 0 && (
          <div style={{ marginBottom: 16 }}>
            <strong>Transactions</strong>
            {results.length > 0 && results.length < totalRows && (
              <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>
                (showing {displayList.length} processed of {totalRows} total)
              </span>
            )}
            <ul
              style={{
                maxHeight: 320,
                overflow: 'auto',
                listStyle: 'none',
                paddingLeft: 0,
                border: '1px solid #ddd',
                background: '#fafafa',
                marginTop: 8,
              }}
            >
              {displayList.map((row, i) => {
                const result = results[i];
                const status = result ? result.status : (loading ? '…' : '—');
                const comment = result ? result.comment : (loading ? 'Processing…' : '');
                return (
                  <li
                    key={row.payment_intent_id + i}
                    style={{
                      padding: '8px 12px',
                      borderBottom: i < displayList.length - 1 ? '1px solid #eee' : 'none',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontWeight: 500, minWidth: 24 }}>{i + 1}.</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }} title={row.payment_intent_id}>
                      {row.payment_intent_id.length > 28 ? row.payment_intent_id.slice(0, 28) + '…' : row.payment_intent_id}
                    </span>
                    <span
                      style={{
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontSize: 12,
                        background:
                          status === 'succeeded'
                            ? '#e6f4ea'
                            : status === 'error' || status === 'failed'
                            ? '#fce8e6'
                            : status === 'requires_action'
                            ? '#fef7e0'
                            : status === 'dry_run'
                            ? '#e8f0fe'
                            : '#e8eaed',
                        color: '#1a1a1a',
                      }}
                    >
                      {status}
                    </span>
                    {comment && <span style={{ fontSize: 12, color: '#555' }}>{comment}</span>}
                  </li>
                );
              })}
              <li ref={listEndRef} />
            </ul>
          </div>
        )}

        {log.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <strong>Log</strong>
            <pre
              style={{
                maxHeight: 240,
                overflow: 'auto',
                background: '#f5f5f5',
                padding: 12,
                fontSize: 12,
                border: '1px solid #ddd',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {log.join('\n')}
            </pre>
          </div>
        )}

        {results.length > 0 && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button onClick={handleDownloadResult}>Download result CSV (with retry details)</Button>
            <Button onClick={handleCopyToClipboard} style={{ marginLeft: 0 }}>
              {copySuccess ? 'Copied!' : 'Copy CSV to clipboard'}
            </Button>
            <span style={{ fontSize: 12, color: '#666' }}>
              {results.length} result(s). If download fails, use Copy to clipboard and paste into a file.
            </span>
          </div>
        )}
      </Container>
    </CardContainer>
  );
}